import { catchError, firstValueFrom, switchMap } from "rxjs";

import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { LogService } from "@bitwarden/common/platform/abstractions/log.service";
import { SdkService } from "@bitwarden/common/platform/abstractions/sdk/sdk.service";
import type {
  AccessDecisionRequest,
  AccessRequestId,
  AccessRequestRevokeRequest,
  AccessRequestView,
} from "@bitwarden/sdk-internal";

import { ApprovalSdkService } from "../abstractions/approval-sdk.service";

export class ApprovalsSdkService implements ApprovalSdkService {
  constructor(
    private sdkService: SdkService,
    private accountService: AccountService,
    private logService: LogService,
  ) {}

  async listInbox(): Promise<AccessRequestView[]> {
    const userId = await firstValueFrom(this.accountService.activeAccount$.pipe(getUserId));
    return firstValueFrom(
      this.sdkService.userClient$(userId).pipe(
        switchMap(async (sdk) => {
          using ref = sdk.take();
          return await ref.value.commercial().pam().approvals().list_inbox();
        }),
        catchError((error: unknown) => {
          this.logService.error(`Failed to list the approval inbox: ${error}`);
          throw error;
        }),
      ),
    );
  }

  async listHistory(): Promise<AccessRequestView[]> {
    const userId = await firstValueFrom(this.accountService.activeAccount$.pipe(getUserId));
    return firstValueFrom(
      this.sdkService.userClient$(userId).pipe(
        switchMap(async (sdk) => {
          using ref = sdk.take();
          return await ref.value.commercial().pam().approvals().list_history();
        }),
        catchError((error: unknown) => {
          this.logService.error(`Failed to list the approval history: ${error}`);
          throw error;
        }),
      ),
    );
  }

  async decide(id: AccessRequestId, request: AccessDecisionRequest): Promise<AccessRequestView> {
    const userId = await firstValueFrom(this.accountService.activeAccount$.pipe(getUserId));
    return firstValueFrom(
      this.sdkService.userClient$(userId).pipe(
        switchMap(async (sdk) => {
          using ref = sdk.take();
          return await ref.value.commercial().pam().approvals().decide(id, request);
        }),
        catchError((error: unknown) => {
          // The request may carry an approver-authored comment; never log it.
          this.logService.error(`Failed to record the approval decision: ${error}`);
          throw error;
        }),
      ),
    );
  }

  async revoke(id: AccessRequestId, request: AccessRequestRevokeRequest): Promise<void> {
    const userId = await firstValueFrom(this.accountService.activeAccount$.pipe(getUserId));
    await firstValueFrom(
      this.sdkService.userClient$(userId).pipe(
        switchMap(async (sdk) => {
          using ref = sdk.take();
          return await ref.value.commercial().pam().approvals().revoke(id, request);
        }),
        catchError((error: unknown) => {
          // The request carries the approver's reason; never log it.
          this.logService.error(`Failed to revoke the approval: ${error}`);
          throw error;
        }),
      ),
    );
  }
}
